import { Subscription } from "rxjs";
import { Weaver } from "../../aop";
import { BusinessObjectBase } from "../../bo/src/business-object-base";
import { EventBus } from "../../event";
import { ConsoleLogger } from "../../logger";
import { ModelBase, PropertyType } from "../../model";
import { PropertyChangingJoinPoint } from "./joinpoints/property-changing";
import { ReadonlyEngine } from "./readonly-engine";

type OmitRule<T extends ModelBase> = {
	effectOn: PropertyType<T>,
	func: (model: T) => boolean
};

export const ReadonlyCheckerUnexpectedValueChangeEvent = 'ReadonlyCheckerUnexpectedValueChangeEvent';

export class ReadonlyChecker<TBO extends BusinessObjectBase<TModel>, TModel extends ModelBase> {
	private readonly logger = new ConsoleLogger(this.constructor.name);
	private attached = false;

	private propertyJoinPointSubscription!: Function;
	private stateSubscription!: Subscription;

	private omitRules: OmitRule<any>[] = [];

	public constructor(
		private readonly readonlyEngine: ReadonlyEngine<TBO, TModel>,
		private readonly weaver: Weaver
	) { }

	public attach(): void {
		if (this.attached) {
			this.logger.error('is already attached');
			return;
		}

		this.propertyJoinPointSubscription = this.weaver.register(PropertyChangingJoinPoint, ctx => {
			const isReadonly = () => this.isPropertyOnReadonly(ctx.model, ctx.propertyName);
			if (!isReadonly()) return;

			if (this.isPropertyReadonlyOmmitedByRule(ctx.model, ctx.propertyName)) return;

			if (this.readonlyEngine.executeRuleForProperty(ctx.propertyName as keyof ModelBase, ctx.model)) {
				const message = `Unexpected value change - property: ${ctx.propertyName}. ${'Change has been stopped.'}`;
				this.logger.error(message);
				EventBus.emit(ReadonlyCheckerUnexpectedValueChangeEvent, {message, propertyName: ctx.propertyName});
				ctx.cancel();
			}
		});

		this.attached = true;
	}

	private isPropertyOnReadonly(source: ModelBase, propertyName: string): boolean {
		return !!this.readonlyEngine.readonlyProperties.find(elem => (elem.source === source) && (propertyName === elem.propertyName) && (elem.readonly));
	}

	private isPropertyReadonlyOmmitedByRule(source: ModelBase, propertyName: string): boolean {
		return !!this.omitRules.find(elem => ((elem.effectOn[0] === source.constructor) || elem.effectOn[0].isPrototypeOf(source.constructor)) && (elem.effectOn[1] === propertyName) && elem.func(source));
	}


	public dispose(): void {
		this.stateSubscription?.unsubscribe();
		this.propertyJoinPointSubscription?.();
	}

	public addOmitRule<T extends ModelBase>(
        effectOn: PropertyType<T>[],
		func: (model: T) => boolean = () => true
	): void {
		effectOn.forEach(elem => {
			const rule: OmitRule<T> = {effectOn: elem, func};
			this.omitRules.push(rule);
		});
	}

}
